import { useEffect, useState } from "react";
import { Copy, GripVertical, Trash2 } from "lucide-react";
import cloneDeep from "lodash/cloneDeep";
import { v4 as uuidv4 } from "uuid";
import { Mileage } from "../../../Helpers/mileage";
import Alert from "../../components/Alert";
import FormField from "../../components/FormField";
import IconButton from "../../components/IconButton";
import SearchSelect from "../../components/SearchSelect";
import TextInput from "../../components/TextInput";
import Toggle from "../../components/Toggle";

const firstError = (errors, key) => Array.isArray(errors?.[key]) ? errors[key][0] : errors?.[key];
const LineAtSite = (props) => {
  const { handleInputChange } = props;
  const [errors, setErrors] = useState([{}]);

  const lineData = props.lineData;
  const lineIndex = props.lineIndex;
  const worksiteData = props.worksiteData;
  const worksiteIndex = props.worksiteIndex;
  const possessionIndex = props.possessionIndex;
  const conversionUnit = props.conversionUnit;
  const unitRoundFactor = conversionUnit === "milesYards" ? 4 : conversionUnit === "milesChains" ? 2 : 3;
  const mileageLabel = conversionUnit === "milesYards" ? "Miles / yards" : conversionUnit === "milesChains" ? "Miles / chains" : "Km / metres";
  const valuePath = `possessions.${possessionIndex}.worksites.${worksiteIndex}.lines_at_site.${lineIndex}`;

  useEffect(() => {
    if (!lineData.line_reference) {
      handleInputChange(`${valuePath}.line_reference`, uuidv4(), { systemSync: true })
    }
  }, [handleInputChange, lineData.line_reference, valuePath]);

  const handleCloneLine = () => {
    const existingWorksiteLines = cloneDeep(worksiteData?.lines_at_site || []);
    let existingLine = cloneDeep(lineData);
    existingLine.name = "";
    existingLine.line_reference = "";

    props.handleInputChange(`possessions.${possessionIndex}.worksites.${worksiteIndex}.lines_at_site`, [...existingWorksiteLines, existingLine]);
  };

  const handleSelectElr = (path, e) => {
    const elrData = !e ? '' : {
      elr: e.elr,
      boundaries: {
        start: {
          milesYards: e?.boundaries?.start?.milesYards,
          milesChains: e?.boundaries?.start?.milesChains,
          kilometersMeters: e?.boundaries?.start?.kilometersMeters,
        },
        end: {
          milesYards: e?.boundaries?.end?.milesYards,
          milesChains: e?.boundaries?.end?.milesChains,
          kilometersMeters: e?.boundaries?.end?.kilometersMeters,
        }
      }
    }

    props.handleInputChange(path, elrData);
  };

  const handleMileageChange = (path, e) => {
    const mileageValue = typeof e?.target?.value !== 'undefined' ? Mileage.inputValue(e, props.conversionUnit) : e;
    const mileageData = {
      milesYards: mileageValue ? Mileage.convertMileage(mileageValue, props.conversionUnit, "milesYards", mileageValue.startsWith('-')) : '',
      milesChains: mileageValue ? Mileage.convertMileage(mileageValue, props.conversionUnit, "milesChains", mileageValue.startsWith('-')) : '',
      kilometersMeters: mileageValue ? Mileage.convertMileage(mileageValue, props.conversionUnit, "kilometersMeters", mileageValue.startsWith('-')) : '',
    }

    props.handleInputChange(path, mileageData);
  };

  const validateMileages = (lineData, e) => {
    if (lineData?.[e?.target?.name]?.[props.conversionUnit] && !isNaN(lineData[e.target.name][props.conversionUnit])) {
      const updatedMileage = parseFloat(Mileage.formatMileages(lineData[e.target.name][props.conversionUnit], props.conversionUnit, true)).toFixed(unitRoundFactor).toString();
      handleMileageChange(`${valuePath}.${e.target.name}`, updatedMileage);
    }

    checkMileages(lineData);
  };

  // Stepping with the arrows never blurs the field, so check the stepped value straight away
  const handleMileageInput = (name, e) => {
    handleMileageChange(`${valuePath}.${name}`, e);

    if (Mileage.isStepChange(e)) {
      checkMileages({ ...lineData, [name]: { ...lineData?.[name], [props.conversionUnit]: Mileage.inputValue(e, props.conversionUnit) } });
    }
  };

  const checkMileages = (lineData) => {
    const errorMessages = {
      start: false,
      end: false,
      poss_start: false,
      poss_end: false,
      h2l_invalid: false,
    };

    // lineData still holds the values as typed, so pad them the same way before comparing against the boundaries
    const paddedMileage = (name) => {
      const mileage = lineData?.[name]?.[props.conversionUnit];
      return mileage && !isNaN(mileage) ? parseFloat(Mileage.formatMileages(mileage, props.conversionUnit, true)).toFixed(unitRoundFactor).toString() : mileage;
    };

    const startMiles = paddedMileage("start");
    const endMiles = paddedMileage("end");
    const possStartMiles = paddedMileage("poss_start");
    const possEndMiles = paddedMileage("poss_end");

    const fromStartBoundary = lineData?.from_elr?.boundaries?.start?.[props.conversionUnit] ?? undefined;
    const fromEndBoundary = lineData?.from_elr?.boundaries?.end?.[props.conversionUnit] ?? undefined;
    const toStartBoundary = lineData?.to_elr?.boundaries?.start?.[props.conversionUnit] ?? undefined;
    const toEndBoundary = lineData?.to_elr?.boundaries?.end?.[props.conversionUnit] ?? undefined;

    if (fromStartBoundary === undefined || fromEndBoundary === undefined || toStartBoundary === undefined  || toEndBoundary === undefined ) {
      return errorMessages;
    }
  
    if (parseFloat(startMiles) < parseFloat(fromStartBoundary) || parseFloat(startMiles) > parseFloat(fromEndBoundary)) {
      errorMessages.start = true;
    }
  
    if (parseFloat(endMiles) < parseFloat(toStartBoundary) || parseFloat(endMiles) > parseFloat(toEndBoundary)) {
      errorMessages.end = true;
    }
  
    if (parseFloat(possStartMiles) < parseFloat(fromStartBoundary) || parseFloat(possStartMiles) > parseFloat(fromEndBoundary)) {
      errorMessages.poss_start = true;
    }
  
    if (parseFloat(possEndMiles) < parseFloat(toStartBoundary) || parseFloat(possEndMiles) > parseFloat(toEndBoundary)) {
      errorMessages.poss_end = true;
    }

    //Ensure Lines at Site mileages fall within the Possession Limits
    if (possStartMiles?.length > 0 &&
        possEndMiles?.length > 0 &&
        errorMessages.start === false &&
        errorMessages.end === false &&
        errorMessages.poss_start === false &&
        errorMessages.poss_end === false
    ) {
      //Handle high to low mileages
      if ((parseFloat(possStartMiles) > parseFloat(possEndMiles) && parseFloat(startMiles) < parseFloat(endMiles)) ||
          (parseFloat(possStartMiles) < parseFloat(possEndMiles) && parseFloat(startMiles) > parseFloat(endMiles)
      )) {
        errorMessages.h2l_invalid = true;
      }
    }
  
    setErrors(errorMessages);
  };
            
  const linePath = `data.possessions.${possessionIndex}.worksites.${worksiteIndex}.lines_at_site.${lineIndex}`;
  const fieldPath = (field) => `${linePath}.${field}`;
  const lineError = firstError(props.errors, linePath);
  const elrOptions = (props.activeElrs || []).map((elr) => ({ label: elr.elr, value: elr.elr, data: elr }));
  const selectedElr = (value) => elrOptions.find((option) => option.value === value?.elr) || null;
  const mileageField = (name, label) => (
    <FormField error={firstError(props.errors, fieldPath(`${name}.${conversionUnit}`))} id={`line-${possessionIndex}-${worksiteIndex}-${lineIndex}-${name}`} label={`${label} (${mileageLabel})`} required>
      {(fieldProps) => <TextInput {...fieldProps} className={(errors?.[name] || errors?.h2l_invalid) ? "wsm-input--error" : ""} name={name} onBlur={(event) => validateMileages(lineData, event)} onChange={(event) => handleMileageInput(name, event)} step={unitRoundFactor === 4 ? "0.0001" : unitRoundFactor === 3 ? "0.001" : "0.01"} type="number" value={lineData?.[name]?.[conversionUnit] || ""} />}
    </FormField>
  );

  return (
    <div className="wsm-line-editor__body">
      {lineError && <Alert title={`Line ${lineIndex + 1}`} type="error">{lineError}</Alert>}
      {lineData?.from_elr?.elr && lineData?.to_elr?.elr && (
        <Alert title="Selected ELR boundaries" type="info">
          {lineData.from_elr.elr === lineData.to_elr.elr
            ? `${lineData.from_elr.elr}: ${lineData.from_elr.boundaries?.start?.[conversionUnit]} to ${lineData.from_elr.boundaries?.end?.[conversionUnit]}`
            : `${lineData.from_elr.elr}: ${lineData.from_elr.boundaries?.start?.[conversionUnit]} to ${lineData.from_elr.boundaries?.end?.[conversionUnit]}; ${lineData.to_elr.elr}: ${lineData.to_elr.boundaries?.start?.[conversionUnit]} to ${lineData.to_elr.boundaries?.end?.[conversionUnit]}`}
        </Alert>
      )}
      {(errors?.start || errors?.end || errors?.poss_start || errors?.poss_end) && <Alert type="warning">Ensure mileages fall within the selected ELR boundaries.</Alert>}
      {errors?.h2l_invalid && <Alert type="warning">Lines-at-site and possession-limit mileages must use the same direction.</Alert>}

      <div className="wsm-line-editor__fields wsm-line-editor__fields--two">
        <SearchSelect error={firstError(props.errors, fieldPath("from_elr.elr"))} id={`line-from-elr-${possessionIndex}-${worksiteIndex}-${lineIndex}`} label="From ELR" loading={props.elrsLoading} onChange={(option) => handleSelectElr(`${valuePath}.from_elr`, option?.data)} options={elrOptions} required value={selectedElr(lineData.from_elr)} />
        <SearchSelect error={firstError(props.errors, fieldPath("to_elr.elr"))} id={`line-to-elr-${possessionIndex}-${worksiteIndex}-${lineIndex}`} label="To ELR" loading={props.elrsLoading} onChange={(option) => handleSelectElr(`${valuePath}.to_elr`, option?.data)} options={elrOptions} required value={selectedElr(lineData.to_elr)} />
        {mileageField("start", "Start mileage")}
        {mileageField("end", "End mileage")}
      </div>

      <FormField error={firstError(props.errors, fieldPath("name"))} id={`line-name-${possessionIndex}-${worksiteIndex}-${lineIndex}`} label="Line name" required>
        {(fieldProps) => <TextInput {...fieldProps} onChange={(event) => props.handleInputChange(`${valuePath}.name`, event.target.value)} value={lineData.name || ""} />}
      </FormField>

      <div className="wsm-line-editor__fields wsm-line-editor__fields--two">
        <Toggle checked={lineData.marker_board_start === 1} description="Required at the start mileage." label="Start marker board" onChange={(event) => props.handleInputChange(`${valuePath}.marker_board_start`, event.target.checked ? 1 : 0)} />
        <Toggle checked={lineData.marker_board_end === 1} description="Required at the end mileage." label="End marker board" onChange={(event) => props.handleInputChange(`${valuePath}.marker_board_end`, event.target.checked ? 1 : 0)} />
      </div>

      <fieldset className="wsm-line-editor__limits">
        <legend>Possession limits</legend>
        <div className="wsm-line-editor__fields wsm-line-editor__fields--two">
          {mileageField("poss_start", "Start mileage")}
          {mileageField("poss_end", "End mileage")}
        </div>
      </fieldset>

      <div className="wsm-item-actions wsm-item-actions--line">
        <IconButton aria-label={`Reorder line ${lineIndex + 1}`} disabled={(props.allLinesAtSite || []).length < 2} icon={GripVertical} size="compact" variant="ghost" {...props.dragHandleProps} />
        <IconButton aria-label={`Duplicate line ${lineIndex + 1}`} icon={Copy} onClick={handleCloneLine} size="compact" variant="ghost" />
        <IconButton aria-label={`Remove line ${lineIndex + 1}`} disabled={lineIndex === 0} icon={Trash2} onClick={() => props.handleRemoveFormRow(`possessions.${possessionIndex}.worksites.${worksiteIndex}.lines_at_site`, lineIndex, true, "line")} size="compact" variant="destructive" />
      </div>
    </div>
  );
};

export default LineAtSite;
